import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { useRef } from 'react'
import { type icons } from '@opallabs/constellation'
import { Size } from '@opallabs/gem'
import { Button, Icon } from '@opallabs/lapidary'
import { useGemButtonClearance } from '../gemButtonClearance'
import { type CalendarResultCounts } from '../resultCounts'
import { type CalendarItemKind } from '../types'
import * as styles from './styles'

/** The marks the grid already draws on a bar and on a chip, in the grid's order. */
const KIND_MARKS: readonly Readonly<{
  count: keyof CalendarResultCounts
  glyph: keyof typeof icons.gem
  kind: CalendarItemKind
}>[] = [
  { count: 'moments', glyph: 'gem-moment-outlined', kind: 'moment' },
  { count: 'content', glyph: 'gem-content-outlined', kind: 'content' },
]

const summaryOf = (
  { content, moments }: CalendarResultCounts,
  shownKinds: readonly CalendarItemKind[]
): string =>
  [
    shownKinds.includes('moment') &&
      `${moments} ${moments === 1 ? 'moment' : 'moments'}`,
    shownKinds.includes('content') && `${content} content`,
  ]
    .filter(Boolean)
    .join(', ') + ' shown'

export type DrawerFooterProps = Readonly<{
  counts: CalendarResultCounts
  /** Nothing is filtered, so there is nothing to clear. */
  isEmpty: boolean
  onClearAll: () => void
  onDone: () => void
  /** A kind the calendar does not draw always counts 0, so it is left out. */
  shownKinds: readonly CalendarItemKind[]
}>

/**
 * The bar under every pane of the drawer.
 *
 * A value takes effect as it is chosen, so Done only shuts the drawer — there
 * is nothing here to save. Clear all speaks for every category rather than the
 * one in front of a person, because the filter somebody wants gone is rarely
 * set on the tier they are standing on.
 *
 * The summary is what the filters have come to. It is a picture of the calendar
 * behind the drawer, so the marks are drawn for the eye and the whole sentence
 * is left for a reader. It ends where the Gem button starts, which is the one
 * thing on the page drawn over the drawer.
 */
export const DrawerFooter: React.FC<DrawerFooterProps> = ({
  counts,
  isEmpty,
  onClearAll,
  onDone,
  shownKinds,
}) => {
  const gemButtonClearance = useGemButtonClearance()
  const doneRef = useRef<HTMLButtonElement>(null)

  return (
    <footer
      className={styles.selectors.footer}
      style={{ paddingInlineEnd: gemButtonClearance }}
    >
      <Button ref={doneRef} buttonText="Done" name="Done" onClick={onDone} />
      <Button
        buttonText="Clear all"
        disabled={isEmpty}
        name="Clear all filters"
        onClick={() => {
          // Clearing turns this button off, and a button that turns off drops
          // the focus it holds, out of the drawer and onto the page.
          doneRef.current?.focus()
          onClearAll()
        }}
        variant="ghost"
      />

      <div className={styles.selectors.footerSummary}>
        <VisuallyHidden>{summaryOf(counts, shownKinds)}</VisuallyHidden>
        {KIND_MARKS.filter(({ kind }) => shownKinds.includes(kind)).map(
          ({ count, glyph }) => (
            <span key={count} aria-hidden={true}>
              <Icon ariaHidden={true} glyph={glyph} size={Size.Icon} />
              <span className={styles.selectors.text}>{counts[count]}</span>
            </span>
          )
        )}
      </div>
    </footer>
  )
}
DrawerFooter.displayName = 'Calendar: DrawerFooter'
